import * as cx from "classnames/bind";
import {
  Accordion,
  Button,
  ButtonLink,
  Container,
  GalleryCarousel,
  ProductCarousel,
  Section,
  ToolsCarousel,
} from "components";
import React, { useEffect } from "react";
import { useCart } from "hooks";
import { HomeLayout } from "layouts";
import Head from "next/head";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { push } from "@socialgouv/matomo-next";
import s from "styles/Qkit.module.scss";
import countryToCurrency from "country-to-currency";
import Cookies from "js-cookie";

const COCKTAIL_COUNT = 8;
const TOOL_COUNT = 17;
const SLIDE_COUNT = 18;

export default function Home({ csrfToken, qKit }) {
  const { t } = useTranslation();
  const cocktails = Array.from(Array(COCKTAIL_COUNT).keys());
  const tools = Array.from(Array(TOOL_COUNT).keys());
  const slides = Array.from(Array(SLIDE_COUNT).keys());

  const country = Cookies.get("__country") || "ES";

  const currency = qKit.prices[(countryToCurrency[country] || "").toLowerCase()]
    ? countryToCurrency[country].toLowerCase()
    : "eur";
  const price = qKit.prices[currency];
  const router = useRouter();

  const { cart, addToCart } = useCart();

  const ldJsonProduct = {
    "@context": "http://schema.org",
    "@type": "Product",
    brand: {
      "@type": "Brand",
      name: "Cocktail Club",
    },
    color: "black",
    image: {
      "@type": "ImageObject",
      url: "https://cocktailclub.com/img/kit1.jpg",
      width: 800,
      height: "800",
    },
    itemCondition: "http://schema.org/NewCondition",
    manufacturer: "Cocktail Club",
    name: "Cocktail kit - 17 essential tools for your home bar set",
    offers: [
      {
        "@type": "Offer",
        availability: "http://schema.org/InStock",
        price: `${price.unit_amount / 100.0}`,
        priceCurrency: `${currency.toUpperCase()}`,
        sku: `${qKit.id}`,
        priceValidUntil: "2023-01-01",
        itemCondition: "https://schema.org/NewCondition",
        url: "https://cocktailclub.com/bar-set",
      },
    ],
    review: [],
    sku: `${qKit.id}`,
    url: "https://cocktailclub.com/bar-set",
    description:
      "The bar set is the most comprehensive cocktail making solution on the market – bundled with our step-by-step video recipes.",
  };
  const ldJsonCrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://cocktailclub.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Bar set",
        item: "https://cocktailclub.com/bar-set",
      },
    ],
  };

  useEffect(() => {
    push([
      "setEcommerceView",
      qKit.id,
      "Q-kit - 17 essential tools for your home bar set",
      4999.0,
    ]);
    push(["trackPageView"]);
  }, []);

  const onAddToCart = async () => {
    push(["trackEvent", "Ecommerce", "Add to Cart", "Add to cart"]);
    push([
      "addEcommerceItem",
      qKit.id,
      "Q-kit - 17 essential tools for your home bar set",
      4999.0,
    ]);
    push(["trackEcommerceCartUpdate", 4999.0]);
    await addToCart(qKit, currency);
    router.push("/cart");
  };
  return (
    <HomeLayout className={cx(s.Qkit)}>
      <Head>
        <title key="title">{qKit.name}</title>
        <meta
          key="description"
          name="description"
          content="The bar set is the most comprehensive cocktail making solution on the market – bundled with our step-by-step video recipes. Free shipping."
        />
        <meta
          key="ogUrl"
          property="og:url"
          content="https://cocktailclub.com/bar-set"
        />
        <link
          key="canonical"
          rel="canonical"
          href="https://cocktailclub.com/bar-set"
        />

        <meta key="ogType" property="og:type" content="product" />
        <meta key="ogTitle" property="og:title" content={qKit.name} />
        <meta
          key="ogDescription"
          property="og:description"
          content="The bar set is the most comprehensive cocktail making solution on the market – bundled with our step-by-step video recipes. Free shipping."
        />

        <meta
          key="ogImage"
          property="og:image"
          content="https://cocktailclub.com/img/kit1.jpg"
        />
        <meta
          property="og:image:alt"
          content="Bar set| Bar tools | Cocktail Club"
        />

        <meta property="product:section" content="Bar tools" />
        <meta
          property="product:tag"
          content="Bar set | Bar tools | Cocktail Club"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJsonProduct) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJsonCrumbs) }}
        />
      </Head>
      <Section id="homeTop">
        <style jsx>{`
          #homeTop {
            padding-top: 60px;
          }
        `}</style>
        <Container>
          <div className="flex flex-wrap">
            <div className={cx(s.QKit_images)}>
              <ProductCarousel slides={slides} />
            </div>
            <div className={cx(s.QKit_info)}>
              <h1 className="Heading__main">
                {t("Bar set / 17 essential tools for your home bar set")}
              </h1>
              <p className="m0">{t("Cocktail kit with video recipes")}</p>
              <br />
              <p className="Heading__price Cyan">
                <bdi>
                  {price.unit_amount / 100.0}
                  &nbsp;
                  <span>{currency.toUpperCase()}</span>
                </bdi>
              </p>
              <small>{t("Local tax may apply on delivery")}</small>
              <hr />
              <h2>
                {t(
                  "The Cocktail Club bar set is the most comprehensive cocktail making solution on the market – bundled with our step-by-step video recipes",
                )}
              </h2>
              <ul className={cx(s.productInfoList)}>
                <li>
                  <p>
                    {t(
                      "Everything you need to mix any cocktail – bar toolset and recipes",
                    )}
                  </p>
                </li>
                <li>
                  <p>
                    {t(
                      "17 must-have premium tools make up the ultimate home bar set",
                    )}
                  </p>
                </li>
                <li>
                  <p>
                    {t(
                      "Features a washable roll-bag, cutting board and ice bucket",
                    )}
                  </p>
                </li>
                <li>
                  <p>{t("High-end Nordic design with portability in mind")}</p>
                </li>
                <li>
                  <p>
                    {t(
                      "The bar set is integrated with our bottle scanner and recipe app",
                    )}
                  </p>
                </li>
              </ul>

              <br />
              <h3 className="Heading__sub m0">
                {t("Free shipping")}: {t("3 - 5 days")}
              </h3>
              <div
                className="Button Primary Full mt18"
                size="far"
                svg="shopping-cart"
                onClick={onAddToCart}
                id="btn_add_to_cart"
              >
                {t("Add to cart")}
              </div>
            </div>
          </div>
        </Container>
      </Section>
      <Section>
        <Container>
          <h2 className="Heading">{t("Additional information")}</h2>
          <div className={cx(s.kitTable)}>
            <p>
              <span>{t("Weight")}</span>
              {t("4.80 kg")}
            </p>
            <p>
              <span>{t("Dimensions")}</span>
              {t("40 x 23 x 18 cm")}
            </p>
            <p>
              <span>{t("Available colors")}</span>
              {t("Black")}
            </p>
            <p>
              <span>{t("Includes")}</span>
              <span>
                {t(
                  "Ice Bucket, Cutting Board, Hawthorne Strainer, Ice Scoop, Jigger, Julep Strainer, Fine Strainer, Knife, Mexican Elbow Juicer, Mixing Glass, Muddler, Shaker, Speed Pourer, Bar Spoon, Bottle Opener, Fruit Zezter",
                )}
              </span>
            </p>
            <p>
              <span>{t("Delivery")}</span>
              {t("3 - 5 days in Norway | 7 - 14 days International")}
            </p>
          </div>
        </Container>
      </Section>
      <Section>
        <Container>
          <h2 className="Heading mb8">
            {t("17 premium bar tools in the set")}
          </h2>
          <p>{t("See all 17 premium bar tools")}</p>
          <ToolsCarousel tools={tools} />
        </Container>
      </Section>
      {/*
      <Section>
        <Container>
          <GalleryCarousel cocktails={cocktails} />
        </Container>
      </Section>
      */}
      <Section>
        <Container>
          <div className="flex flex-wrap Rad ofh">
            <div className="split-item-50__d">
              <div className="imgRes">
                <Image
                  alt="Cocktail Club | Quality & price"
                  src="/img/kit1.jpg"
                  objectFit="cover"
                  layout="responsive"
                  height={900}
                  width={900}
                />
              </div>
            </div>
            <div
              className="split-item-50__d flex align-center p32"
              style={{ background: "#ffffff" }}
            >
              <div className="contentInner">
                <h4 className="Heading mb8">{t("Quality & pricing")}</h4>
                <p>
                  {t(
                    "Our cocktail kit is today used by professional bartenders as well as amateurs in over 40 countries. We’ve made sure anyone can approve of the kit and its content.",
                  )}
                </p>
                <p>
                  {t(
                    "From the reassuringly thick steel in the shaker to our self-designed muddler with the perfect grip, the combination of the tools, ice box and our app is a cocktail-making marriage made in heaven.",
                  )}
                </p>
                <p>
                  {t(
                    "All the tools included in the bar set is also used in our recipe tutorials, helping users to easily master the recipes and familiarize themselves with the hardware.",
                  )}
                </p>
              </div>
            </div>
          </div>
        </Container>
      </Section>
      <Section>
        <Container className="Narrow center">
          <h2 className="Heading mb8">
            {t("Each recipe comes with a step-by-step video tutorial")}
          </h2>
          <p>
            {t(
              "We know how hard it can be mixing perfect serves at home sometimes.",
            )}
          </p>
          <p>
            {t(
              "Our mantra at Cocktail Club is ‘drink better, not more’, and our quest to demystify great home bartending inspired us to create video tutorials with each expert recipe we feature.",
            )}
          </p>
          <p>
            {t(
              "Follow the easy, step-by-step videos on our app and you’ll be serving friends, family and yourself fabulous cocktails at home in no time.",
            )}
          </p>
          <p>
            {t(
              "Use our game-changing bottle scanner to link to them, or simply search by spirit ingredients, and we’ll hook you up with a host of inspirational expert tutorials to take your home bartending to amazing new heights.",
            )}
          </p>
        </Container>
      </Section>
      <Section>
        <Container className="center">
          <video
            controls
            autoPlay
            muted
            playsInline
            loop
            className="m__auto Rad ofh"
          >
            <source
              src={`${process.env.NEXT_PUBLIC_S3_BUCKET}vid/pages/q-kit-by-cocktail-club.mp4`}
              type="video/mp4"
            />
          </video>
        </Container>
      </Section>
      <Section>
        <Container>
          <div className="flex flex-wrap row-reverse__d Rad ofh">
            <div className="split-item-50__d">
              <div className="imgRes">
                <Image
                  alt="Cocktail Club | Story part 1"
                  src="/img/story1.jpg"
                  objectFit="cover"
                  layout="responsive"
                  height={900}
                  width={900}
                />
              </div>
            </div>
            <div
              className="split-item-50__d flex align-center p32"
              style={{ background: "#ffffff" }}
            >
              <div className="contentInner">
                <h4 className="Heading">
                  {t(
                    "The bar set story: a love of great cocktails meets Nordic cool",
                  )}
                </h4>
                <p>
                  {t(
                    "Our bar set is designed by renowned Norwegian design house Baard Eker, whose brief was to help us create the world’s best cocktail kit.",
                  )}
                </p>
                <p>
                  {t(
                    "From the outset, we focused on that most important of all ingredients in great cocktail-making – ice. Ice should always be in abundance and kept super-cold, of course.",
                  )}
                </p>
                <p>
                  {t(
                    "From here, we curated a set of only the best quality bar tools – we knew we needed uncompromisingly great implements in order to satisfy not only discerning home cocktail-makers but also professional bartenders.",
                  )}
                </p>
                <p>
                  {t(
                    "We’re proud to say that the combination of the bar set’s high-end design with our quest to get folk drinking better at home via our game-changing app has already provided countless fabulous cocktail moments all over the world.",
                  )}
                </p>
                <p>
                  {t(
                    "We gladly raise our glasses to many more to come. Cheers!",
                  )}
                </p>
              </div>
            </div>
          </div>
        </Container>
      </Section>
      <Section>
        <Container>
          <h2 className="Heading">{t("Have any questions?")}</h2>
          <p>
            {t(
              "Here is a quick summary of the frequently asked questions our users have regarding our bar set.",
            )}
          </p>
          <Accordion
            title={t("Is it complicated?")}
            content={t(
              "No, it’s fun and easy. Just follow the recipe tutorials and you’ll be mixing in no time! We’ve actually made it so easy that you will be serving your guests fantastic cocktails on your first try.",
            )}
          />
          <Accordion
            title={t("What tools do I need?")}
            content={t(
              "All the tools you need for any cocktail is in the bar set. If you purchase the bar set we’ll give you full access to the recipes included in the price.",
            )}
          />
          <Accordion
            title={t("Is there a free version?")}
            content={t(
              "Of course! We have opened up all recipes for you to enjoy free. Just sign-up here and get full access.",
            )}
          />
          <Accordion
            title={t("Return policy?")}
            content={t(
              "If for some reason there is something wrong with the bar set, one of the tools or you just want to return it, please contact us on info@cocktailclub.com and we will help you.",
            )}
          />
          <Accordion
            title={t("How do I prepare for mixing?")}
            content={t(
              "Well, the best thing is to plan ahead. Buy your ingredients, make your menu if you are having guests, and make your syrup.<br/><br/>Syrup by the way is simple sugar syrup made by adding 1 part sugar and 1 equal part water. Heat and stir until transparent. Keep in the fridge and use it when needed. It lasts for several weeks. Check out our tips <a href='/' target='_blank'>here</a>",
            )}
          />
        </Container>
      </Section>
    </HomeLayout>
  );
}

export const getStaticProps = async ({ locale }) => {
  const products = await fetch(
    `${process.env.NEXT_PUBLIC_REST_API_URL}stripe/products`,
  ).then((r) => r.json());
  return {
    props: {
      qKit:
        products[process.env.NEXT_PUBLIC_Q_KIT_STRIPE_ID] ||
        products[Object.keys(products)[0]],
      ...(await serverSideTranslations(locale)),
    },
  };
};
